import React, { Component } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { LocationAutocompleteInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './LocationFilter.module.css';

const identity = v => v;

/**
 * LocationFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The ID
 * @param {string} props.name - The name
 * @param {React.Node} props.label - The label
 * @param {Function} props.getAriaLabel - The function to retrieve the aria label for the component
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to submit
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class LocationFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationValue: null,
    };
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      getAriaLabel,
      initialValues,
      contentPlacementOffset = 0,
      onSubmit,
      intl,
      showAsPopup = true,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const rawInitialAddress = initialValues?.address;
    const rawInitialBounds = initialValues?.bounds;
    const hasInitialValues = !!rawInitialAddress;
    
    const labelForPopup = hasInitialValues
      ? intl.formatMessage({ id: 'LocationFilter.labelSelected' }, { labelText: rawInitialAddress })
      : label;

    const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
    const labelForPlain = <span className={labelClass}>{label}</span>;

    const filterText = intl.formatMessage({ id: 'LocationFilter.filterText' });
    const placeholder = intl.formatMessage({ id: 'LocationFilter.placeholder' });

    const namedInitialValues = rawInitialAddress && rawInitialBounds ? {
      location: {
        search: rawInitialAddress,
        selectedPlace: {
          address: rawInitialAddress,
          bounds: rawInitialBounds
        }
      }
    } : {};

    const handleSubmit = values => {
      const locationData = values?.location;
      
      // Only submit if a place was actually selected
      if (locationData && locationData.selectedPlace) {
        const { address, bounds } = locationData.selectedPlace;
        onSubmit({ address, bounds });
      } else if (!locationData || (!locationData.search && !locationData.selectedPlace)) {
        // Only clear if completely empty (no search text and no selected place)
        onSubmit({ address: null, bounds: null });
      }
      // Don't submit anything if user is still typing (has search text but no selectedPlace)
    };

    return showAsPopup ? (
      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <FilterPopup
          className={classes}
          rootClassName={rootClassName}
          popupClassName={css.popupSize}
          name={name}
          label={labelForPopup}
          ariaLabel={getAriaLabel(label, rawInitialAddress)}
          isSelected={hasInitialValues}
          id={`${id}.popup`}
          showAsPopup
          labelMaxWidth={250}
          contentPlacementOffset={contentPlacementOffset}
          onSubmit={handleSubmit}
          initialValues={namedInitialValues}
          keepDirtyOnReinitialize
          {...rest}
        >
        <div className={css.fieldWrapper}>
          <label className={css.fieldLabel} htmlFor={`${id}-input`}>
            {filterText}
          </label>
          <Field name="location" format={identity}>
            {({ input, meta }) => {
              const { onChange, ...restInput } = input;
              const searchOnChange = value => {
                // Update form state only, don't trigger any navigation or submission
                // User needs to click the Submit button in popup mode
                onChange(value);
              };
              return (
                <LocationAutocompleteInput
                  className={css.field}
                  inputClassName={css.locationInput}
                  predictionsClassName={css.predictions}
                  iconClassName={css.searchIcon}
                  placeholder={placeholder}
                  closeOnBlur={false}
                  useDarkText={true}
                  id={`${id}-input`}
                  input={{ 
                    ...restInput, 
                    onChange: searchOnChange,
                    onFocus: (e) => {
                      // Prevent any scroll or navigation behavior on focus
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (restInput.onFocus) restInput.onFocus(e);
                    },
                    onBlur: (e) => {
                      // Prevent popup from closing
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (restInput.onBlur) restInput.onBlur(e);
                    }
                  }}
                  meta={meta}
                />
              );
            }}
          </Field>
        </div>
      </FilterPopup>
      </div>
    ) : (
      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={labelForPlain}
        ariaLabel={getAriaLabel(label, rawInitialAddress)}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        {...rest}
      >
        <div className={css.fieldPlain}>
          <label className={css.fieldPlainLabel} htmlFor={`${id}-input`}>
            {filterText}
          </label>
          <Field name="location" format={identity}>
            {({ input, meta, form }) => {
              const { onChange, ...restInput } = input;
              const searchOnChange = value => {
                // Update form state - this will trigger FormSpy -> handleSubmit
                onChange(value);
                
                // For liveEdit mode, only submit if a place is actually selected
                // Don't submit on focus or when just typing
                if (value && value.selectedPlace && value.selectedPlace.address) {
                  const { address, bounds } = value.selectedPlace;
                  setTimeout(() => {
                    onSubmit({ address, bounds });
                  }, 50);
                }
                // If value is empty or just has search text (no selection), don't submit
              };
              return (
                <LocationAutocompleteInput
                  className={css.fieldPlainInput}
                  inputClassName={css.locationInput}
                  predictionsClassName={css.predictions}
                  iconClassName={css.searchIcon}
                  placeholder={placeholder}
                  closeOnBlur={false}
                  useDarkText={true}
                  input={{ 
                    ...restInput, 
                    onChange: searchOnChange,
                    onFocus: (e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (restInput.onFocus) restInput.onFocus(e);
                    },
                    onBlur: (e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (restInput.onBlur) restInput.onBlur(e);
                    }
                  }}
                  meta={meta}
                />
              );
            }}
          </Field>
        </div>
      </FilterPlain>
      </div>
    );
  }
}

export default injectIntl(LocationFilter);

